import { useContext, useRef, useState } from "react";

import { toast } from "react-toastify";

import { GlobalContext } from "../../Context/GlobalContext";

const useBanner = () => {
  const { api, userID } = useContext(GlobalContext);

  const [status, setStatus] = useState(null);

  /* =========================================================
     REQUEST LOCKS

     Same API same time 2 baar fire hone se prevent.
  ========================================================= */

  const fetchLockRef = useRef(false);

  const createLockRef = useRef(false);

  /*
    Delete/update per banner ID lock.

    Example:
    Banner 10 delete chal raha hai
    to Banner 10 ka second delete blocked.

    Lekin Banner 11 independently work kar sakta hai.
  */

  const deleteLockRef = useRef(new Set());

  const updateLockRef = useRef(new Set());

  /* =========================================================
     COMMON POST REQUEST
  ========================================================= */

  const postRequest = async (endpoint, payload = {}) => {
    const response = await api.post(endpoint, payload);

    return response?.data;
  };

  /* =========================================================
     FETCH STATUS
  ========================================================= */

  const fetchStatus = async () => {
    try {
      const data = await postRequest("/Inventory/v2.0/getBannerStatus", {});

      if (data?.responseCode === "00") {
        setStatus(data?.response);

        return true;
      }

      toast.error(data?.responseMessage || "Failed to fetch status");

      return false;
    } catch (err) {
      console.log("Status API error:", err);

      toast.error("Status API error");

      return false;
    }
  };

  /* =========================================================
     FETCH BANNERS

     GET API duplicate protection.
  ========================================================= */

  const fetchBanner = async (setFetchedBanner) => {
    if (!userID) {
      return false;
    }

    /*
        Already request running?
        Second request block.
      */

    if (fetchLockRef.current) {
      console.log("Duplicate banner GET blocked");

      return false;
    }

    fetchLockRef.current = true;

    try {
      const payload = {
        userid: userID,

        status: "A",
      };

      console.log("GET BANNER PAYLOAD:", payload);

      const res = await api.post(
        "/Inventory/v2.0/getCardLayoutGrocery",
        payload,
      );

      console.log("GET BANNER RESPONSE:", res?.data);

      if (res.data?.responseCode === "00") {
        setFetchedBanner(res.data?.response || []);

        return true;
      }

      toast.error(res.data?.responseMessage || "Failed");

      return false;
    } catch (err) {
      console.log("Fetch banner error:", err);

      toast.error("Server error");

      return false;
    } finally {
      fetchLockRef.current = false;
    }
  };

  /* =========================================================
     UPDATE BANNER STATUS

     NO fetchBanner here.
  ========================================================= */

  const updateBannerStatus = async (item, newStatus) => {
    const cardId = item?.id;

    if (!cardId) {
      toast.error("Banner ID missing");

      return false;
    }

    /*
        Same banner already updating?
      */

    if (updateLockRef.current.has(cardId)) {
      console.log("Duplicate update blocked:", cardId);

      return false;
    }

    updateLockRef.current.add(cardId);

    try {
      const payload = {
        /*
            IMPORTANT:

            fk_userid = A1211 type value ho sakta hai.

            Number("A1211") = NaN

            Isliye Number() nahi lagana.
          */

        userid: item?.fk_userid || userID,

        cardid: Number(cardId),

        status: newStatus,
      };

      console.log("UPDATE BANNER PAYLOAD:", payload);

      const res = await api.post(
        "/Inventory/v2.0/updateCardLayoutGroceryStatus",
        payload,
      );

      console.log("UPDATE BANNER RESPONSE:", res?.data);

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Status updated");

        return true;
      }

      toast.error(res.data?.responseMessage || "Failed");

      return false;
    } catch (err) {
      console.log("Update banner error:", err);

      toast.error("Server error");

      return false;
    } finally {
      updateLockRef.current.delete(cardId);
    }
  };

  /* =========================================================
     CREATE BANNER

     INSERT duplicate protection.
  ========================================================= */

  const createBanner = async (formData) => {
    if (!userID) {
      toast.error("User ID missing");

      return false;
    }

    /*
        INSERT already running?
        Block second request.
      */

    if (createLockRef.current) {
      console.log("Duplicate create banner request blocked");

      return false;
    }

    createLockRef.current = true;

    try {
      const payload = {
        userid: userID,

        cardCount: formData?.cardCount || "",

        html: formData?.html || "",

        title: formData?.title || "",

        status: formData?.status || "N",
      };

      console.log("CREATE BANNER PAYLOAD:", payload);

      const res = await api.post(
        "/Inventory/v2.0/insertCardLayoutGrocery",
        payload,
      );

      console.log("CREATE BANNER RESPONSE:", res?.data);

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Banner created");

        return true;
      }

      toast.error(res.data?.responseMessage || "Failed");

      return false;
    } catch (err) {
      console.log("Create banner error:", err);

      toast.error("Server error");

      return false;
    } finally {
      createLockRef.current = false;
    }
  };

  /* =========================================================
     DELETE BANNER

     DELETE duplicate protection.

     NO fetchBanner here.
  ========================================================= */

  const deleteBanner = async (id) => {
    if (!id) {
      toast.error("Banner ID missing");

      return false;
    }

    /*
        Same ID already deleting?
      */

    if (deleteLockRef.current.has(id)) {
      console.log("Duplicate delete blocked:", id);

      return false;
    }

    deleteLockRef.current.add(id);

    try {
      const payload = {
        id: String(id),
      };

      console.log("DELETE BANNER PAYLOAD:", payload);

      const res = await api.post("/Inventory/deleteGroceryLayoutV2", payload);

      console.log("DELETE BANNER RESPONSE:", res?.data);

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Banner deleted");

        return true;
      }

      toast.error(res.data?.responseMessage || "Delete failed");

      return false;
    } catch (err) {
      console.log("Delete banner error:", err);

      toast.error("Server error");

      return false;
    } finally {
      deleteLockRef.current.delete(id);
    }
  };

  /* =========================================================
     RETURN
  ========================================================= */

  return {
    fetchBanner,

    updateBannerStatus,

    fetchStatus,

    status,

    createBanner,

    deleteBanner,
  };
};

export default useBanner;
